import { Box, Button, Typography } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { radius } from "src/design/tokens";
import { fontMono, fontStretch } from "src/design/typography";
import { HeroPanel } from "src/grading/components/HeroBand/HeroBand";
import InlineAlert from "src/grading/components/InlineAlert/InlineAlert";
import {
  useGenerateJoinCodeMutation,
  useRevokeJoinCodeMutation,
} from "src/store/reducers/sessions/sessionsApiSlice";

export interface JoinCodeCardProps {
  sessionId: string;
  code: string | null;
  connectedCount?: number;
  /** Hides the New code and Revoke buttons when false. */
  canManage?: boolean;
  onRegenerated?: (code: string) => void;
  /**
   * `card` (default): white card with QR code and pill buttons.
   * `hero`: the design's translucent "Scorers join with" panel on a hero band, with the code in
   * big condensed numerals and New code / Revoke as text links.
   */
  variant?: "card" | "hero";
}

/** Session join code: QR, station URL, connected count and manage actions. */
export const JoinCodeCard = ({
  sessionId,
  code,
  connectedCount,
  canManage = true,
  onRegenerated,
  variant = "card",
}: JoinCodeCardProps) => {
  const { t } = useTranslation();
  const [generate, { isLoading: generating }] = useGenerateJoinCodeMutation();
  const [revoke, { isLoading: revoking }] = useRevokeJoinCodeMutation();
  const [failed, setFailed] = useState(false);
  const origin = window.location.origin;

  const handleGenerate = async () => {
    setFailed(false);
    try {
      const result = await generate(sessionId).unwrap();
      onRegenerated?.(result.code);
    } catch {
      setFailed(true);
    }
  };

  const handleRevoke = async () => {
    setFailed(false);
    try {
      await revoke(sessionId).unwrap();
    } catch {
      setFailed(true);
    }
  };

  const connectedText =
    code && connectedCount !== undefined
      ? t("sessions.connected", {
          count: connectedCount,
          defaultValue: "{{count}} connected",
        })
      : null;

  if (variant === "hero") {
    const linkSx = {
      p: 0,
      minWidth: 0,
      minHeight: 0,
      fontSize: 12,
      fontWeight: 700,
      color: "heroInk",
      textDecoration: "underline",
      "&:hover": { textDecoration: "underline", bgcolor: "transparent" },
    } as const;
    return (
      <HeroPanel>
        <Typography
          component="span"
          sx={{
            fontSize: 10.5,
            fontWeight: 700,
            lineHeight: 1,
            letterSpacing: ".14em",
            textTransform: "uppercase",
            color: "heroMuted",
          }}
        >
          {t("today.scorersJoinWith", { defaultValue: "Scorers join with" })}
        </Typography>
        {code ? (
          <Box
            component="output"
            aria-live="polite"
            sx={{
              display: "block",
              fontSize: 42,
              fontWeight: 800,
              lineHeight: 1,
              letterSpacing: ".14em",
              fontStretch: fontStretch.condensed,
            }}
          >
            {code}
          </Box>
        ) : (
          <Typography sx={{ fontSize: 13, color: "heroMuted" }}>
            {t("sessions.noActiveCode")}
          </Typography>
        )}
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            alignItems: "center",
            flexWrap: "wrap",
            fontSize: 12,
            fontWeight: 500,
            color: "heroMuted",
          }}
        >
          {canManage ? (
            <Button
              variant="text"
              disabled={generating}
              onClick={handleGenerate}
              sx={linkSx}
            >
              {t("sessions.regenerateCode")}
            </Button>
          ) : null}
          {canManage && code ? (
            <Button
              variant="text"
              disabled={revoking}
              onClick={handleRevoke}
              sx={linkSx}
            >
              {t("sessions.revokeCodeShort", { defaultValue: "Revoke" })}
            </Button>
          ) : null}
          <span>
            {[`${origin}/station`, connectedText].filter(Boolean).join(" · ")}
          </span>
        </Box>
        {failed ? (
          <InlineAlert severity="error">
            {t("sessions.joinCodeError", {
              defaultValue: "Could not update the join code. Try again.",
            })}
          </InlineAlert>
        ) : null}
      </HeroPanel>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: 1,
        borderColor: "divider",
        borderRadius: `${radius.card}px`,
        p: 2.5,
        display: "flex",
        flexWrap: "wrap",
        gap: 2.5,
        alignItems: "center",
      }}
    >
      <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
        <Typography
          variant="overline"
          sx={{ display: "block", color: "text.secondary" }}
        >
          {t("sessions.joinCode")}
        </Typography>
        {code ? (
          <Box
            component="output"
            aria-live="polite"
            sx={{
              display: "block",
              fontFamily: fontMono,
              fontSize: 28,
              fontWeight: 700,
              letterSpacing: ".12em",
              color: "text.primary",
            }}
          >
            {code}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t("sessions.noActiveCode")}
          </Typography>
        )}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 0.5, wordBreak: "break-all" }}
        >
          {`${origin}/station`}
        </Typography>
        {connectedText ? (
          <Typography variant="body2" color="text.secondary">
            {connectedText}
          </Typography>
        ) : null}
        {canManage ? (
          <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
            <Button
              variant="outlined"
              disabled={generating}
              onClick={handleGenerate}
            >
              {t("sessions.regenerateCode")}
            </Button>
            {code ? (
              <Button
                variant="outlined"
                color="error"
                disabled={revoking}
                onClick={handleRevoke}
              >
                {t("sessions.revokeCodeShort", { defaultValue: "Revoke" })}
              </Button>
            ) : null}
          </Box>
        ) : null}
        {failed ? (
          <Box sx={{ mt: 1.5 }}>
            <InlineAlert severity="error">
              {t("sessions.joinCodeError", {
                defaultValue: "Could not update the join code. Try again.",
              })}
            </InlineAlert>
          </Box>
        ) : null}
      </Box>
      {code ? (
        <Box
          sx={{
            // QR codes need a light backdrop in both modes; onTier is white in light and dark.
            bgcolor: "onTier",
            p: 1,
            borderRadius: `${radius.control}px`,
            lineHeight: 0,
          }}
        >
          <QRCodeSVG
            value={`${origin}/station?code=${code}`}
            size={112}
            title={t("sessions.joinCode")}
          />
        </Box>
      ) : null}
    </Box>
  );
};

export default JoinCodeCard;
